import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fadeInUp, staggerContainer, viewportConfig } from "@/lib/animations";
import inductusLogo from "@/assets/inductus-logo.webp";

const footerLinks = {
  company: [
    { name: "About", href: "/about" },
    { name: "How We Work", href: "/how-we-work" },
    { name: "Insights", href: "/insights" },
    { name: "Contact", href: "/contact" },
  ],
  services: [
    { name: "Solutions", href: "/solutions" },
    { name: "Who We Work With", href: "/who-we-work-with" },
  ],
  legal: [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Engagement", href: "/terms" },
  ],
};

export const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground">
      <motion.div
        className="section-container py-16 lg:py-20"
        initial="hidden"
        whileInView="visible"
        viewport={viewportConfig}
        variants={staggerContainer}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <motion.div className="lg:col-span-1" variants={fadeInUp}>
            <Link to="/" className="inline-block mb-4">
              <img
                src={inductusLogo}
                alt="Inductus International"
                width={144}
                height={40}
                className="h-10 w-auto transition-transform duration-200 hover:scale-[1.02]"
                loading="eager"
                decoding="async"
              />
            </Link>
            <p className="font-sans text-sm text-primary-foreground/70 leading-relaxed mb-6">
              Strategic, Operational & AI Advisory
            </p>
            <p className="font-sans text-xs text-primary-foreground/50">
              Registered Office: Sheridan, WY 82801, USA
              <br />
              Serving businesses across the United States
            </p>
          </motion.div>

          {/* Company Links */}
          <motion.div variants={fadeInUp}>
            <h4 className="font-sans text-xs font-semibold uppercase tracking-wider mb-6 text-primary-foreground/50">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="font-sans text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services Links */}
          <motion.div variants={fadeInUp}>
            <h4 className="font-sans text-xs font-semibold uppercase tracking-wider mb-6 text-primary-foreground/50">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="font-sans text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal Links */}
          <motion.div variants={fadeInUp}>
            <h4 className="font-sans text-xs font-semibold uppercase tracking-wider mb-6 text-primary-foreground/50">
              Legal
            </h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="font-sans text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="mt-16 pt-8 border-t border-primary-foreground/10"
          variants={fadeInUp}
        >
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-sans text-xs text-primary-foreground/50">
              © {new Date().getFullYear()} Inductus International LLC. All rights reserved.
            </p>
            <motion.div whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
              <Link
                to="/request-assessment"
                className="font-sans text-sm font-medium text-primary-foreground hover:text-primary-foreground/80 transition-colors"
              >
                Request a Strategic Assessment →
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
};
